// hooks/useCompanyLogo.js
import { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';

const API_BASE_URL = 'https://api.jobeng.ai';

// ── Derive up-to-2 initials from a company name ───────────────────────────
const getInitials = (name = '') => {
  if (!name?.trim()) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

export const useCompanyLogo = (refreshOnFocus = true) => {
  const [logo,        setLogo]        = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const loadLogo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Serve from cache immediately (offline-safe, instant render)
      const cachedRaw = await AsyncStorage.getItem('company_profile');
      if (cachedRaw) {
        const profile = JSON.parse(cachedRaw);
        setLogo(profile.logo || null);
        setCompanyName(profile.name || profile.company_name || '');
      }

      // 2. Fetch latest from API and update both state + cache
      const [token, companyId] = await Promise.all([
        AsyncStorage.getItem('auth_token'),
        AsyncStorage.getItem('company_id'),
      ]);

      if (token && companyId) {
        const response = await fetch(
          `${API_BASE_URL}/api/data/company_profiles?company_id=${parseInt(companyId, 10)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type':  'application/json',
              'Accept':        'application/json',
              'X-API-Key':     '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const json = await response.json();
          const profiles = json?.data;
          const latest   = Array.isArray(profiles) && profiles.length > 0
            ? profiles[0]
            : null;

          if (latest) {
            setLogo(latest.logo || null);
            setCompanyName(latest.name || latest.company_name || '');
            // Merge into cache without blowing away other stored fields
            const base    = cachedRaw ? JSON.parse(cachedRaw) : {};
            const merged  = { ...base, ...latest };
            await AsyncStorage.setItem('company_profile', JSON.stringify(merged));
          }
        }
      }
    } catch (err) {
      console.warn('[useCompanyLogo] Error loading logo:', err);
      setError(err);
      // Keep whatever was loaded from cache — don't clear it on network error
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadLogo();
  }, [loadLogo]);

  // Reload when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      if (refreshOnFocus) {
        loadLogo();
      }
    }, [loadLogo, refreshOnFocus])
  );

  return {
    logo,
    companyName,
    initials: getInitials(companyName),
    loading,
    error,
    reload: loadLogo,
  };
};